import { signInWithPopup, GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { auth, provider } from "../firebase";

/**
 * Auth Service - Puppeteer-Interceptable Authentication
 *
 * Provides a unified authentication interface that works in both:
 * 1. Normal browser (uses Firebase popup)
 * 2. Puppeteer/Electron environment (interceptable for external browser auth)
 */
export const authService = {
  /**
   * Detects if running inside Puppeteer/Electron environment
   * Puppeteer should inject: window.__EDUTOOLS_PUPPETEER__ = true
   */
  isInPuppeteer: () => {
    return window.__EDUTOOLS_PUPPETEER__ === true;
  },

  /**
   * Main sign-in method - routes to appropriate auth flow
   * @returns {Promise<UserCredential>} Firebase user credential
   */
  signInWithGoogle: async () => {
    if (authService.isInPuppeteer()) {
      console.log('[AuthService] Puppeteer environment detected - using interceptable flow');
      return await authService.signInViaPuppeteer();
    } else {
      console.log('[AuthService] Normal browser - using popup flow');
      return await authService.signInViaPopup();
    }
  },

  /**
   * Normal Firebase popup authentication
   * Used in regular browser environments
   */
  signInViaPopup: async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      return result;
    } catch (error) {
      console.error('[AuthService] Popup sign-in failed:', error);
      throw error;
    }
  },

  /**
   * Puppeteer-interceptable authentication flow
   * Sets flags that Puppeteer monitors, then waits for Puppeteer to resolve
   */
  signInViaPuppeteer: async () => {
    return new Promise((resolve, reject) => {
      try {
        // Set the auth request flag for Puppeteer to detect
        window.__EDUTOOLS_AUTH_REQUEST__ = {
          type: 'google-signin',
          timestamp: Date.now()
        };

        // Store resolve/reject handlers for Puppeteer to call
        window.__EDUTOOLS_AUTH_RESOLVE__ = resolve;
        window.__EDUTOOLS_AUTH_REJECT__ = reject;

        console.log('[AuthService] Auth request set - waiting for Puppeteer to handle');

        // Optional: Add timeout to prevent hanging forever
        const timeout = setTimeout(() => {
          reject(new Error('Authentication timeout - no response from Puppeteer after 60 seconds'));
          authService.cleanupAuthRequest();
        }, 60000); // 60 second timeout

        // Store timeout ID so it can be cleared when auth completes
        window.__EDUTOOLS_AUTH_TIMEOUT__ = timeout;

      } catch (error) {
        console.error('[AuthService] Error setting up Puppeteer auth:', error);
        reject(error);
      }
    });
  },

  /**
   * Called by Electron after successful authentication
   * Resolves the pending promise with user credentials
   *
   * @param {Object} authData - Authentication data from Electron
   * Simple format: { success: true, timestamp?, email?, uid?, displayName?, photoURL? }
   * Firebase format (optional): { idToken: string, accessToken: string }
   */
  handleAuthComplete: async (authData) => {
    try {
      console.log('[AuthService] Auth completion received from Electron', authData);

      // Clear timeout if exists
      if (window.__EDUTOOLS_AUTH_TIMEOUT__) {
        clearTimeout(window.__EDUTOOLS_AUTH_TIMEOUT__);
      }

      // PRIMARY PATH: Simple success callback - trust Electron and skip Firebase
      if (authData.success === true) {
        console.log('[AuthService] Success callback received - trusting Electron auth');

        // Get user data from authData or localStorage fallback
        const email = authData.email || localStorage.getItem('hyperlinksEmail') || 'unknown@edutools.com';
        const uid = authData.uid || authData.email || Date.now().toString();
        const displayName = authData.displayName || authData.email || 'User';
        const photoURL = authData.photoURL || '';

        console.log('[AuthService] Creating mock user object for:', email);

        // Create a mock user object that satisfies the expected interface
        const mockUser = {
          uid: uid,
          email: email,
          displayName: displayName,
          photoURL: photoURL,
          emailVerified: true,
          isAnonymous: false,
          metadata: {
            creationTime: new Date().toISOString(),
            lastSignInTime: new Date().toISOString(),
          },
          providerData: [{
            providerId: 'electron',
            uid: uid,
            displayName: displayName,
            email: email,
            photoURL: photoURL,
          }],
          // Flag to indicate this is custom auth (not Firebase)
          _isElectronAuth: true,
        };

        const result = {
          user: mockUser,
          credential: null,
          operationType: 'signIn',
          _additionalUserInfo: {
            isNewUser: false,
            providerId: 'electron',
          },
        };

        // Store minimal auth state
        const authState = {
          loggedIn: true,
          timestamp: authData.timestamp || Date.now(),
          provider: 'electron',
          email: email,
        };
        localStorage.setItem('edutools_auth_state', JSON.stringify(authState));

        console.log('[AuthService] Auth state stored, resolving promise');

        // Resolve the promise
        if (window.__EDUTOOLS_AUTH_RESOLVE__) {
          window.__EDUTOOLS_AUTH_RESOLVE__(result);
        }

        // Cleanup
        authService.cleanupAuthRequest();

        return result;
      }

      // FALLBACK PATH: Full Firebase authentication with tokens
      else if (authData.idToken && authData.accessToken) {
        console.log('[AuthService] Processing authentication with Firebase tokens');

        // Create Firebase credential from Google tokens
        const credential = GoogleAuthProvider.credential(
          authData.idToken,
          authData.accessToken
        );

        // Sign in to Firebase with the credential
        const result = await signInWithCredential(auth, credential);
        console.log('[AuthService] Firebase sign-in successful:', result.user.email);

        // Resolve the promise
        if (window.__EDUTOOLS_AUTH_RESOLVE__) {
          window.__EDUTOOLS_AUTH_RESOLVE__(result);
        }

        // Cleanup
        authService.cleanupAuthRequest();

        return result;
      }

      // ERROR PATH: Invalid data format
      else {
        throw new Error(
          'Invalid authData format. Expected: {success: true} or {idToken, accessToken}'
        );
      }

    } catch (error) {
      console.error('[AuthService] Error completing auth:', error);

      // Reject the promise
      if (window.__EDUTOOLS_AUTH_REJECT__) {
        window.__EDUTOOLS_AUTH_REJECT__(error);
      }

      // Cleanup
      authService.cleanupAuthRequest();

      throw error;
    }
  },

  /**
   * Called by Puppeteer if authentication fails
   * Rejects the pending promise
   */
  handleAuthError: (error) => {
    console.error('[AuthService] Auth error received from Puppeteer:', error);

    // Clear timeout
    if (window.__EDUTOOLS_AUTH_TIMEOUT__) {
      clearTimeout(window.__EDUTOOLS_AUTH_TIMEOUT__);
    }

    // Reject the promise
    if (window.__EDUTOOLS_AUTH_REJECT__) {
      window.__EDUTOOLS_AUTH_REJECT__(error);
    }

    // Cleanup
    authService.cleanupAuthRequest();
  },

  /**
   * Cleanup function to remove auth request flags
   */
  cleanupAuthRequest: () => {
    delete window.__EDUTOOLS_AUTH_REQUEST__;
    delete window.__EDUTOOLS_AUTH_RESOLVE__;
    delete window.__EDUTOOLS_AUTH_REJECT__;
    delete window.__EDUTOOLS_AUTH_TIMEOUT__;
  },

  /**
   * Get the current auth state
   */
  getAuthState: () => {
    try {
      const state = localStorage.getItem('edutools_auth_state');
      return state ? JSON.parse(state) : null;
    } catch (e) {
      return null;
    }
  },

  /**
   * Clear the auth state
   */
  clearAuthState: () => {
    localStorage.removeItem('edutools_auth_state');
  }
};

// Make authService globally accessible for Puppeteer to call
if (typeof window !== 'undefined') {
  window.authService = authService;
}

export default authService;
